/**
 * TagPicker — Modal dialog for toggling tags on selected tasks.
 *
 * Opened via Cmd+Shift+T keyboard shortcut or programmatically.
 * Shows all existing workspace tags with checkmarks indicating
 * whether the tag is applied to the selected tasks.
 * Supports creating new tags inline by typing + Enter.
 *
 * Keyboard: ↑↓ navigate, Enter toggle/create, Escape close.
 */

import { memo, useState, useRef, useCallback, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { db } from "@/db/schema"
import { updateTaskTags } from "@/db/operations"
import { useLiveQueryImmediate } from "@/db/hooks"
import { useAllTags } from "@/features/task-detail/hooks/useAllTags"
import { useTagColors } from "@/features/tags/useTagColors"
import { Dialog, DialogContent, DialogTitle } from "@/app/components/ui/dialog"
import CheckIcon from "@/shared/icons/pikaicons/stroke/general/check-tick-single.svg?react"
import {
  PickerList,
  PickerSearch,
  PickerItem,
  PickerEmpty,
} from "@/shared/components/picker"

interface TagPickerProps {
  /** Task IDs to apply tag changes to */
  taskIds: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const TagPicker = memo(function TagPicker({
  taskIds,
  open,
  onOpenChange,
}: TagPickerProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [allTags] = useAllTags()
  const { tagColors } = useTagColors()

  // Fetch current tags for all selected tasks (live)
  const [taskTagMap] = useLiveQueryImmediate(
    async () => {
      if (taskIds.length === 0) return new Map<string, string[]>()
      const tasks = await db.totodo.bulkGet(taskIds)
      const map = new Map<string, string[]>()
      for (const task of tasks) {
        if (task?.id) map.set(task.id, task.tags ?? [])
      }
      return map
    },
    [taskIds.join(",")],
    new Map<string, string[]>(),
  )

  // Compute tag state: "all" (every task has it), "some", or "none"
  const tagStateMap = useMemo(() => {
    const result = new Map<string, "all" | "some" | "none">()
    for (const tag of allTags) {
      let count = 0
      for (const tags of taskTagMap.values()) {
        if (tags.includes(tag)) count++
      }
      if (count === 0) result.set(tag, "none")
      else if (count === taskTagMap.size) result.set(tag, "all")
      else result.set(tag, "some")
    }
    return result
  }, [allTags, taskTagMap])

  // Filter tags by query
  const filtered = useMemo(() => {
    if (!query.trim()) return allTags
    const q = query.toLowerCase()
    const prefix: string[] = []
    const contains: string[] = []
    for (const tag of allTags) {
      const lower = tag.toLowerCase()
      if (lower.startsWith(q)) prefix.push(tag)
      else if (lower.includes(q)) contains.push(tag)
    }
    return [...prefix, ...contains]
  }, [allTags, query])

  // Check if query matches an existing tag exactly (case-insensitive)
  const queryMatchesExisting = useMemo(() => {
    if (!query.trim()) return true
    return allTags.some((t) => t.toLowerCase() === query.trim().toLowerCase())
  }, [allTags, query])

  // Total items = filtered tags + optional "create new" row
  const showCreateRow = query.trim().length > 0 && !queryMatchesExisting
  const totalItems = filtered.length + (showCreateRow ? 1 : 0)

  // Auto-select first result when filter changes
  useEffect(() => {
    setHighlightIndex(totalItems > 0 ? 0 : -1)
  }, [totalItems])

  // Reset query when dialog opens
  useEffect(() => {
    if (open) {
      setQuery("")
      setHighlightIndex(-1)
    }
  }, [open])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return
    const el = listRef.current.children[highlightIndex] as
      | HTMLElement
      | undefined
    el?.scrollIntoView({ block: "nearest" })
  }, [highlightIndex])

  const toggleTag = useCallback(
    async (tag: string) => {
      // If all tasks have the tag → remove it; otherwise → add it
      const state = tagStateMap.get(tag) ?? "none"
      const removing = state === "all"

      for (const [taskId, currentTags] of taskTagMap.entries()) {
        let newTags: string[]
        if (removing) {
          newTags = currentTags.filter((t) => t !== tag)
        } else {
          newTags = currentTags.includes(tag)
            ? currentTags
            : [...currentTags, tag]
        }
        await updateTaskTags(taskId, newTags)
      }

      toast(
        removing
          ? t('Removed tag "{{tag}}"', { tag })
          : t('Added tag "{{tag}}"', { tag }),
      )
    },
    [tagStateMap, taskTagMap, t],
  )

  const createAndApplyTag = useCallback(
    async (tag: string) => {
      for (const [taskId, currentTags] of taskTagMap.entries()) {
        if (!currentTags.includes(tag)) {
          await updateTaskTags(taskId, [...currentTags, tag])
        }
      }
      setQuery("")
      toast(t('Added tag "{{tag}}"', { tag }))
    },
    [taskTagMap, t],
  )

  const handleSelect = useCallback(
    (index: number) => {
      if (index < filtered.length) {
        void toggleTag(filtered[index]!)
      } else if (showCreateRow) {
        void createAndApplyTag(query.trim())
      }
    },
    [filtered, showCreateRow, query, toggleTag, createAndApplyTag],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setHighlightIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1))
      } else if (e.key === "Enter") {
        e.preventDefault()
        if (highlightIndex >= 0) handleSelect(highlightIndex)
      }
    },
    [totalItems, highlightIndex, handleSelect],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-sm gap-0 overflow-hidden p-0"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          requestAnimationFrame(() => inputRef.current?.focus())
        }}
      >
        <DialogTitle className="sr-only">{t("Tags")}</DialogTitle>

        {/* Search input */}
        <PickerSearch
          value={query}
          onChange={setQuery}
          onKeyDown={handleKeyDown}
          placeholder={t("Search or create tag…")}
          inputRef={inputRef}
          showIcon={false}
        />

        {/* Tag list */}
        <PickerList ref={listRef} className="max-h-72 py-1">
          {filtered.length === 0 && !showCreateRow && (
            <PickerEmpty>{t("No tags yet")}</PickerEmpty>
          )}
          {filtered.map((tag, idx) => {
            const state = tagStateMap.get(tag) ?? "none"
            const color = tagColors[tag]

            return (
              <PickerItem
                key={tag}
                aria-selected={idx === highlightIndex}
                highlighted={idx === highlightIndex}
                selected={state !== "none"}
                checkmark={false}
                icon={
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{
                      backgroundColor:
                        color ?? "oklch(from var(--primary) 0.7 0.15 h)",
                    }}
                  />
                }
                trailing={
                  <>
                    {state === "all" && (
                      <CheckIcon className="size-4 shrink-0 text-primary" />
                    )}
                    {state === "some" && (
                      <span className="flex size-4 shrink-0 items-center justify-center">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary/50" />
                      </span>
                    )}
                  </>
                }
                onClick={() => handleSelect(idx)}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                {tag}
              </PickerItem>
            )
          })}
          {/* Create new tag row */}
          {showCreateRow && (
            <PickerItem
              aria-selected={highlightIndex === filtered.length}
              highlighted={highlightIndex === filtered.length}
              icon={<span className="text-primary">+</span>}
              checkmark={false}
              onClick={() => handleSelect(filtered.length)}
              onMouseEnter={() => setHighlightIndex(filtered.length)}
            >
              {t('Create "{{tag}}"', { tag: query.trim() })}
            </PickerItem>
          )}
        </PickerList>
      </DialogContent>
    </Dialog>
  )
})
